import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatCurrency, formatDateBR, formatCPF } from "@/lib/api";

function calcDuracao(saida, entrada, dias = 1) {
    if (!saida || !entrada) return "";
    const [h1, m1] = saida.split(":").map(Number);
    const [h2, m2] = entrada.split(":").map(Number);
    if (isNaN(h1) || isNaN(m1) || isNaN(h2) || isNaN(m2)) return "";
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (mins < 0) mins += 24 * 60;
    const extraDays = Math.max(0, (parseInt(dias) || 1) - 1);
    mins += extraDays * 24 * 60;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h >= 24) {
        const d = Math.floor(h / 24);
        const rh = h % 24;
        return `${d}d ${rh}h${String(m).padStart(2, "0")}min`;
    }
    return `${h}h${String(m).padStart(2, "0")}min`;
}
import { Button } from "@/components/ui/button";
import { Printer, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_carpool-manager-6/artifacts/qyxqg7dd_logo%20Padr%C3%A3o.png";

function numberToWordsBR(value) {
    if (value == null) return "";
    const num = Number(value);
    if (isNaN(num)) return "";
    const reais = Math.floor(num);
    const cents = Math.round((num - reais) * 100);
    const unidades = ["", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove",
        "dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"];
    const dezenas = ["", "", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"];
    const centenas = ["", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"];

    const toWords = (n) => {
        if (n === 0) return "zero";
        if (n === 100) return "cem";
        let parts = [];
        const c = Math.floor(n / 100);
        const d = Math.floor((n % 100) / 10);
        const u = n % 10;
        const dUnit = n % 100;
        if (c > 0) parts.push(centenas[c]);
        if (dUnit < 20 && dUnit > 0) {
            parts.push(unidades[dUnit]);
        } else {
            if (d > 0) parts.push(dezenas[d]);
            if (u > 0) parts.push(unidades[u]);
        }
        return parts.join(" e ");
    };

    let result = "";
    if (reais > 0) {
        const thousands = Math.floor(reais / 1000);
        const rest = reais % 1000;
        if (thousands > 0) {
            result += (thousands === 1 ? "mil" : `${toWords(thousands)} mil`);
            if (rest > 0) result += rest < 100 ? " e " : " ";
        }
        if (rest > 0 || thousands === 0) result += toWords(rest);
        result += reais === 1 ? " real" : " reais";
    }
    if (cents > 0) {
        result += reais > 0 ? " e " : "";
        result += `${toWords(cents)} ${cents === 1 ? "centavo" : "centavos"}`;
    }
    if (!result) result = "zero reais";
    return result.charAt(0).toUpperCase() + result.slice(1);
}

export default function TripPrint() {
    const { id } = useParams();
    const { user } = useAuth();
    const [trip, setTrip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get("/trips", { params: {} }).then((res) => {
            const found = res.data.find((t) => t.id === id);
            if (found) {
                setTrip(found);
            } else {
                setError(true);
            }
            setLoading(false);
        }).catch(() => { setError(true); setLoading(false); });
    }, [id]);

    const doPrint = () => window.print();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
        );
    }

    if (error || !trip) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col gap-4">
                <div className="text-muted-foreground">Viagem não encontrada</div>
                <Button onClick={() => window.close()}>Fechar</Button>
            </div>
        );
    }

    const tipoLabel = trip.tipo === "fixo" ? "CONTRATO DE VEÍCULO" : "DIÁRIA";
    const tipoColor = trip.tipo === "fixo" ? "#065f46" : "#0369a1";
    const today = new Date().toLocaleDateString("pt-BR");
    const kmRodados = (trip.km_saida != null && trip.km_chegada != null && trip.km_chegada >= trip.km_saida)
        ? trip.km_chegada - trip.km_saida : null;

    return (
        <div className="print-root min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-8" data-testid="trip-print-page">
            {/* Action bar - hidden on print */}
            <div className="no-print max-w-3xl mx-auto mb-4 flex items-center justify-between">
                <Button variant="outline" onClick={() => window.close()} data-testid="btn-close-print">
                    Fechar
                </Button>
                <Button onClick={doPrint} className="gap-2" data-testid="btn-print-now">
                    <Printer className="w-4 h-4" /> Imprimir
                </Button>
            </div>

            {/* Printable ficha */}
            <div className="ficha max-w-3xl mx-auto bg-white shadow-2xl relative overflow-hidden" data-testid="ficha-content">
                {/* Top accent bar */}
                <div className="h-2" style={{ background: `linear-gradient(90deg, ${tipoColor} 0%, #10b981 100%)` }} />

                {/* Header */}
                <div className="ficha-header flex items-center gap-3 px-5 py-3 border-b border-gray-300 bg-gradient-to-r from-emerald-50 to-white">
                    <div className="w-16 h-16 bg-emerald-900 rounded-lg flex items-center justify-center p-1 shrink-0 shadow-md border-2 border-emerald-800">
                        <img src={LOGO_URL} alt="COOMFLONA" className="w-full h-full object-contain" />
                    </div>
                    <div className="flex-1">
                        <div className="font-extrabold text-base leading-tight text-emerald-900">COOPERATIVA MISTA DA FLONA DO TAPAJÓS</div>
                        <div className="text-xs font-bold text-emerald-800 tracking-wide">COOMFLONA</div>
                        <div className="text-[10px] mt-0.5 text-gray-700">CNPJ 07.315.165/0001-00 • Insc. Est: 15-249-128-7 • coomflona@hotmail.com</div>
                    </div>
                    <div className="text-right shrink-0">
                        <div className="inline-block px-2 py-1 rounded text-[10px] font-bold text-white" style={{ background: tipoColor }}>
                            {tipoLabel}
                        </div>
                        <div className="text-[9px] text-gray-600 mt-1.5 font-mono">FICHA Nº</div>
                        <div className="text-xs font-bold font-mono text-gray-900">{trip.id.slice(0, 8).toUpperCase()}</div>
                    </div>
                </div>

                {/* Section title */}
                <div className="px-5 py-1.5 bg-gray-100 border-b border-gray-300 flex items-center justify-between">
                    <div className="font-bold text-xs text-gray-900 uppercase tracking-wider">Ficha de Controle de Viagem</div>
                    <div className="text-[10px] text-gray-700">Setor de Transporte</div>
                </div>

                {/* Body */}
                <div className="p-5 space-y-2.5 text-sm">
                    {/* SECTION: Responsável */}
                    <div>
                        <SectionTitle>Responsável</SectionTitle>
                        <div className="grid grid-cols-3 gap-3 mt-1.5">
                            <Field label="Nome" value={trip.responsavel_nome} />
                            <Field label="CPF" value={formatCPF(trip.cpf)} />
                            <Field label="Telefone" value={trip.telefone_motorista || "—"} />
                        </div>
                    </div>

                    {/* SECTION: Trajeto */}
                    <div>
                        <SectionTitle>Trajeto</SectionTitle>
                        <div className="mt-1.5 flex items-center gap-2 bg-emerald-50 rounded p-2 border-2 border-emerald-500">
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider text-gray-800 font-bold">Origem</div>
                                <div className="font-extrabold text-gray-900 text-sm">{trip.local_saida || "—"}</div>
                            </div>
                            <div className="text-emerald-800 text-xl font-extrabold">→</div>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider text-gray-800 font-bold">Destino principal</div>
                                <div className="font-extrabold text-gray-900 text-sm">{trip.destino}</div>
                            </div>
                        </div>
                        {trip.destinos_extras && trip.destinos_extras.length > 0 && (
                            <div className="mt-1.5 bg-blue-50 rounded p-2 border border-blue-400">
                                <div className="text-[10px] uppercase tracking-wider text-blue-900 font-bold mb-1">
                                    Destinos adicionais ({trip.destinos_extras.length})
                                </div>
                                <div className="space-y-1">
                                    {trip.destinos_extras.map((d, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm">
                                            <span className="inline-block px-1.5 py-0.5 rounded bg-blue-800 text-white text-[10px] font-bold font-mono">{i + 2}°</span>
                                            <strong className="text-gray-900">{d}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* SECTION: Veículo */}
                    <div>
                        <SectionTitle>Veículo</SectionTitle>
                        <div className="grid grid-cols-2 gap-3 mt-1.5">
                            <Field label="Modelo" value={trip.veiculo_modelo || "—"} />
                            <Field label="Placa" value={<span className="font-mono font-bold">{trip.veiculo_placa}</span>} />
                        </div>
                    </div>

                    {/* SECTION: Datas e horários */}
                    <div>
                        <SectionTitle>Datas e horários</SectionTitle>
                        {trip.datas && trip.datas.length > 1 ? (
                            <div className="mt-1.5 bg-gray-100 rounded p-2 border border-gray-300">
                                <div className="text-[9px] uppercase tracking-wider text-gray-700 font-bold mb-1">Datas da diária ({trip.datas.length} dias)</div>
                                <div className="flex flex-wrap gap-1">
                                    {trip.datas.map((d, i) => (
                                        <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-white border border-gray-500 text-[11px] font-mono font-semibold text-gray-900">{formatDateBR(d)}</span>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-3 mt-1.5">
                                <Field label="Data" value={formatDateBR(trip.data)} />
                                <Field label={trip.tipo === "fixo" ? "Nº de meses" : "Nº de dias"} value={String(trip.dias || 1)} />
                            </div>
                        )}
                        <div className="grid grid-cols-3 gap-3 mt-1.5">
                            <Field label="Saída" value={trip.hora_saida || "—"} />
                            <Field label="Chegada" value={trip.hora_entrada || "—"} />
                            <Field label="Duração" value={calcDuracao(trip.hora_saida, trip.hora_entrada, trip.dias) || "—"} />
                        </div>
                    </div>

                    {/* SECTION: KM */}
                    <div>
                        <SectionTitle>Quilometragem</SectionTitle>
                        <div className="grid grid-cols-3 gap-3 mt-1.5">
                            <Field label="KM saída" value={trip.km_saida != null ? `${trip.km_saida.toLocaleString("pt-BR")} km` : "—"} />
                            <Field label="KM chegada" value={trip.km_chegada != null ? `${trip.km_chegada.toLocaleString("pt-BR")} km` : "—"} />
                            <Field label="KM rodados" value={kmRodados != null ? <strong className="text-emerald-800">{kmRodados.toLocaleString("pt-BR")} km</strong> : "—"} />
                        </div>
                    </div>

                    {/* Período de contrato */}
                    {(trip.contrato_inicio || trip.contrato_fim) && (
                        <div className="bg-blue-50 border-l-4 border-blue-600 rounded p-2">
                            <div className="text-[10px] uppercase tracking-wider text-blue-900 font-bold">Período de Contrato</div>
                            <div className="mt-0.5 text-sm text-gray-900">
                                <strong>{formatDateBR(trip.contrato_inicio)}</strong> a <strong>{formatDateBR(trip.contrato_fim)}</strong>
                            </div>
                        </div>
                    )}

                    {/* Discriminativo mensal para contratos (fixo) */}
                    {trip.tipo === "fixo" && trip.valor_diaria && (
                        <div className="rounded-lg border-2 border-emerald-700 p-3 bg-emerald-50">
                            <div className="text-[10px] uppercase tracking-wider text-emerald-900 font-extrabold border-b border-emerald-700 pb-1 mb-1.5">
                                Discriminativo das parcelas mensais
                            </div>
                            {/* Destaque Valor por mês */}
                            <div className="flex items-center justify-between bg-white border border-emerald-700 rounded-md px-3 py-1.5 mb-2">
                                <div className="text-[10px] uppercase tracking-wider text-emerald-900 font-bold">Valor por mês</div>
                                <div className="text-xl font-extrabold text-emerald-900">{formatCurrency(trip.valor_diaria)}</div>
                            </div>
                            {trip.dias > 1 && (
                                <table className="w-full text-sm">
                                    <tbody>
                                        {Array.from({ length: trip.dias }, (_, i) => (
                                            <tr key={i} className="border-b border-emerald-200 last:border-0">
                                                <td className="py-1 font-bold text-gray-900">{i + 1}º mês</td>
                                                <td className="py-1 text-right font-extrabold text-emerald-900">{formatCurrency(trip.valor_diaria)}</td>
                                            </tr>
                                        ))}
                                        <tr className="bg-emerald-700 text-white font-bold">
                                            <td className="py-1.5 px-1 font-extrabold uppercase text-[11px]">Valor total ({trip.dias} meses)</td>
                                            <td className="py-1.5 px-1 text-right text-base font-extrabold">{formatCurrency(trip.valor_diaria * trip.dias)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {/* SECTION: Valor - highlight */}
                    <div className="rounded-lg p-3 text-white shadow-md" style={{ background: `linear-gradient(135deg, ${tipoColor} 0%, #059669 100%)` }}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                                {trip.valor_diaria && trip.dias > 0 && (
                                    <div className="text-[10px] text-white/90 mb-0.5">
                                        {formatCurrency(trip.valor_diaria)} × {trip.dias} {trip.tipo === "fixo" ? (trip.dias === 1 ? "mês" : "meses") : (trip.dias === 1 ? "dia" : "dias")}
                                    </div>
                                )}
                                <div className="text-[10px] uppercase tracking-wider text-white font-bold">Valor total</div>
                                <div className="text-2xl font-extrabold mt-0.5">{formatCurrency(trip.valor)}</div>
                                <div className="text-[10px] italic text-white/90 mt-0.5">({numberToWordsBR(trip.valor)})</div>
                            </div>
                        </div>
                        {trip.chave_pix && (
                            <div className="mt-2 pt-2 border-t border-white/30 text-sm flex items-center gap-2">
                                <span className="text-[10px] uppercase tracking-wider text-white font-bold">🔑 PIX:</span>
                                <strong className="font-mono">{trip.chave_pix}</strong>
                            </div>
                        )}
                    </div>

                    {trip.observacoes && (
                        <div>
                            <SectionTitle>Observações</SectionTitle>
                            <div className="mt-1 text-sm whitespace-pre-wrap bg-yellow-50 border-l-4 border-yellow-500 rounded p-2 text-gray-900">{trip.observacoes}</div>
                        </div>
                    )}
                </div>

                {/* Signatures */}
                <div className="px-5 pb-3 pt-6 grid grid-cols-3 gap-3">
                    <div className="text-center">
                        <div className="border-t-2 border-black pt-0.5 text-[10px] font-bold text-gray-900">Responsável pelo veículo</div>
                        <div className="text-[9px] text-gray-700 mt-0.5">{trip.responsavel_nome}</div>
                    </div>
                    <div className="text-center">
                        <div className="border-t-2 border-black pt-0.5 text-[10px] font-bold text-gray-900">Setor de Transporte</div>
                        <div className="text-[9px] text-gray-700 mt-0.5">{user?.name || "—"}</div>
                    </div>
                    <div className="text-center">
                        <div className="border-t-2 border-black pt-0.5 text-[10px] font-bold text-gray-900">Setor Financeiro</div>
                        <div className="text-[9px] text-gray-700 mt-0.5">Recebido em ____/____/______</div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-1.5 border-t border-gray-300 bg-gray-100">
                    <div className="flex justify-between items-center text-[9px] text-gray-700">
                        <div>Emitido em: <strong>{today}</strong> {user?.name ? `— por ${user.name}` : ""}</div>
                        <div>Sistema desenvolvido por <strong>Railan Farias</strong> (Cooperado)</div>
                    </div>
                </div>

                {/* Bottom accent bar */}
                <div className="h-1" style={{ background: `linear-gradient(90deg, #10b981 0%, ${tipoColor} 100%)` }} />
            </div>

            <style>{`
                .ficha, .ficha * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                }
                @media print {
                    body { background: white !important; }
                    .no-print { display: none !important; }
                    .print-root { padding: 0 !important; background: white !important; }
                    .ficha { box-shadow: none !important; max-width: 100% !important; width: 100% !important; page-break-inside: avoid; }
                    /* Força textos escuros mais nítidos e evita transparências que somem na impressão */
                    .ficha { color: #000 !important; }
                    .ficha .text-gray-500,
                    .ficha .text-gray-600,
                    .ficha .text-gray-700,
                    .ficha .text-muted-foreground { color: #1f2937 !important; }
                    .ficha [class*="text-emerald-"] { color: #064e3b !important; }
                    .ficha [class*="text-blue-"] { color: #1e3a8a !important; }
                    .ficha .font-mono { font-weight: 700 !important; }
                    /* Bordas de campos mais firmes */
                    .ficha [class*="border-b-2"] { border-bottom-color: #111827 !important; }
                    .ficha [class*="border-t-2"] { border-top-color: #111827 !important; }
                    /* Áreas com fundo colorido: manter texto branco */
                    .ficha [class*="bg-emerald-7"] *,
                    .ficha [class*="bg-emerald-8"] *,
                    .ficha [class*="bg-emerald-9"] *,
                    .ficha [class*="bg-blue-7"] *,
                    .ficha [class*="bg-blue-8"] *,
                    .ficha [class*="bg-blue-9"] *,
                    .ficha [style*="linear-gradient"] * { color: #fff !important; }
                    /* Elementos com opacidade transparente ficam sólidos */
                    .ficha .text-white\\/90,
                    .ficha .text-white\\/80 { color: #ffffff !important; opacity: 1 !important; }
                    @page { size: A4 portrait; margin: 8mm; }
                }
            `}</style>
        </div>
    );
}

function SectionTitle({ children }) {
    return (
        <div className="text-[11px] font-extrabold uppercase tracking-widest text-emerald-900 border-b-2 border-emerald-800 pb-0.5 mt-0.5">
            {children}
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-black font-bold">{label}</div>
            <div className="mt-0.5 font-bold text-black border-b-2 border-gray-700 pb-0.5 min-h-[20px]">{value || "—"}</div>
        </div>
    );
}
